import { useCallback, useState } from "react";
import { useScopedStorage } from "@/hooks/useScopedStorage";

// Per-user preference for the collapsible nav rail.
export const DRAWER_OPEN_PREF_KEY = "nav:drawerOpen";

// The rail starts EXPANDED so labels carry the navigation on a first visit —
// icons alone are ambiguous for a menu this size. Once the user collapses it,
// that choice is remembered until they change it back.
const DEFAULT_OPEN = true;

/**
 * Nav drawer open/closed preference, remembered per user across visits.
 *
 * Mirrors the useState signature: returns `[open, setOpen]`. The setter accepts
 * a boolean or an updater function and persists the result.
 *
 * @returns {[boolean, (next: boolean | ((prev: boolean) => boolean)) => void]}
 */
export function useDrawerPreference() {
  const storage = useScopedStorage();
  const [open, setOpen] = useState(DEFAULT_OPEN);

  // Load the persisted preference once the user scope (userSub) is available,
  // and reload it if the user changes. Adjusting state during render (rather
  // than in an effect) is React's recommended pattern for syncing state to a
  // changing dependency, and avoids an extra render pass.
  const [loadedForSub, setLoadedForSub] = useState(null);
  if (storage.userSub && loadedForSub !== storage.userSub) {
    setLoadedForSub(storage.userSub);
    const stored = storage.get(DRAWER_OPEN_PREF_KEY);
    setOpen(stored != null ? stored === "true" : DEFAULT_OPEN);
  }

  const setOpenPersistent = useCallback(
    (next) => {
      setOpen((prev) => {
        const value = typeof next === "function" ? next(prev) : next;
        storage.set(DRAWER_OPEN_PREF_KEY, String(value));
        return value;
      });
    },
    [storage]
  );

  return [open, setOpenPersistent];
}

export default useDrawerPreference;
